import type { EndEvent } from 'jssip/lib/RTCSession';
import type { IncomingResponse } from 'jssip/lib/SIPMessage';
import { C as jsSipCauses } from 'jssip';

import { RtcFailureType } from 'features/sip/enums';

/**
 * Determines the RTC failure type based on the JSSip end event
 */
export const determineRtcFailureType = (event: EndEvent): RtcFailureType => {
  const { cause, message } = event;
  // Cast to IncomingResponse to access status_code
  const statusCode = (message as IncomingResponse)?.status_code;

  // Technical/Connection failures - these are likely the ones we care most about
  if (cause === jsSipCauses.causes.REQUEST_TIMEOUT) {
    return RtcFailureType.REQUEST_TIMEOUT;
  }
  if (cause === jsSipCauses.causes.WEBRTC_ERROR) {
    return RtcFailureType.WEBRTC_ERROR;
  }
  if (cause === 'transport_error') {
    return RtcFailureType.TRANSPORT_ERROR;
  }
  if (cause === jsSipCauses.causes.NO_ACK) {
    return RtcFailureType.NO_ACK;
  }

  // Server errors
  if (statusCode === 500) {
    return RtcFailureType.SERVER_ERROR_500;
  }

  // Protocol-level failures
  if (cause === jsSipCauses.causes.REJECTED) {
    return RtcFailureType.REJECTED;
  }
  if (cause === jsSipCauses.causes.BYE) {
    return RtcFailureType.BYE;
  }

  // User-facing states - we're less interested in these according to the user
  if (
    cause === jsSipCauses.causes.NO_ANSWER ||
    cause === jsSipCauses.causes.CANCELED ||
    cause === jsSipCauses.causes.BUSY ||
    cause === jsSipCauses.causes.UNAVAILABLE ||
    cause === 'terminated' ||
    statusCode === 600
  ) {
    return RtcFailureType.USER_STATE_FAILURE;
  }

  return RtcFailureType.UNKNOWN;
};
